import { PulseStorage, PulseStorageOptions, StorageChange } from '@pulse/common';
import { Subject, fromEvent } from 'rxjs';
import { filter, map } from 'rxjs/operators';
import { PulseEnvironment } from '../../globals';
import { Quote } from './quote';

export class PulseSyncStorage extends PulseStorage {
    freeSpace$: Subject<void> = new Subject();
    private availableSpace: number | null | undefined = undefined;

    constructor(
        env: PulseEnvironment,
        private storageArea: Storage,
        private quote: Quote,
        config: PulseStorageOptions,
    ) {
        super(config);

        this.calculateAvailableSpace();
        fromEvent(env.win, 'storage')
            .pipe(
                filter((e): e is StorageEvent => Boolean(e) && 'key' in e && 'storageArea' in e),
                filter(
                    (e) =>
                        Boolean(e.key) && e.storageArea === storageArea && this.isStorageKey(e.key),
                ),
                map(
                    (e): StorageChange => ({
                        // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
                        key: this.revertStorageKey(e.key!),
                        value: e.newValue,
                        external: true,
                    }),
                ),
            )
            .subscribe((change) => this.emitChange(change));
    }

    get(key: string): string | null {
        return this.storageArea.getItem(this.getStorageKey(key));
    }

    set(key: string, value: string): void {
        try {
            const newItemSize = new TextEncoder().encode(value).length;
            const enoughSpace =
                this.availableSpace !== null &&
                this.availableSpace !== undefined &&
                this.availableSpace - newItemSize > 100;

            if (enoughSpace || this.availableSpace === null || this.availableSpace === undefined) {
                this.storageArea.setItem(this.getStorageKey(key), value);
                this.emitChange({ key, value, external: false });
            } else {
                this.freeSpace();
            }
        } catch (err) {
            this.freeSpace();
            console.warn(err);
        } finally {
            this.calculateAvailableSpace();
        }
    }

    keys(): string[] {
        return Object.keys(this.storageArea)
            .filter((key) => this.isStorageKey(key))
            .map((key) => this.revertStorageKey(key));
    }

    remove(key: string): void {
        this.storageArea.removeItem(this.getStorageKey(key));
        this.emitChange({ key, value: null, external: false });
    }

    clear() {
        const keys = this.keys();

        for (const key of keys) {
            this.remove(key);
        }
    }

    all(): Record<string, string> {
        return Object.keys(this.storageArea)
            .filter((key) => this.isStorageKey(key))
            .reduce<Record<string, string>>((data, key) => {
                const revertedKey = this.revertStorageKey(key);
                const value = this.storageArea.getItem(key);

                if (typeof value === 'string') {
                    data[revertedKey] = value;
                }

                return data;
            }, {});
    }

    freeSpace() {
        this.freeSpace$.next();
    }

    async calculateAvailableSpace() {
        this.availableSpace = await this.quote.quoteSize();
    }
}
